'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { useConnection } from 'wagmi';
import { Address } from 'viem';
import { ChainType } from '@/constants/chain-specific';
import { chainAggregators } from '@/constants/chain-specific/aggregators';
import { DEX_LIST } from '@/constants/project-specific/farms/dex';
import type { TokenConfigType } from '@/constants/types/currency';
import { useFactoryPositions } from './useFactoryPositions';
export interface LiquidityPosition {
  id: string;
  dex: string;
  dexName: string;
  token0: TokenConfigType;
  token1: TokenConfigType;
  liquidity: string;
  liquidityFormatted: string;
  token0Amount: string;
  token1Amount: string;
  token0AmountFormatted: string;
  token1AmountFormatted: string;
  fees0: string;
  fees1: string;
  fees0Formatted: string;
  fees1Formatted: string;
  totalValueUsd: number;
  feesValueUsd: number;
  apr: number;
  range: {
    tickLower: number;
    tickUpper: number;
    currentTick: number;
    inRange: boolean;
    lowerPrice: string;
    upperPrice: string;
    currentPrice: string;
  };
  version: 'v2' | 'v3';
  chainId: ChainType;
  contractAddress: Address;
  tokenId?: number; // For V3 NFT positions
  pair?: Address; // For V2 LP tokens
  pairAddress?: Address; // Alias for pair (for V2 LP tokens)
}
interface PositionQueryResult {
  positions: LiquidityPosition[];
  totalValueUsd: number;
  totalFeesUsd: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch all liquidity positions for a user across different DEXs
 * Uses factory contract querying instead of subgraphs
 */
export function useLiquidityPositions(chainId) {
  const $ = _c(11);
  const targetChainId = chainId || 109;
  const {
    positions,
    isLoading,
    error,
    refetch
  } = useFactoryPositions(targetChainId);
  let t0;
  if ($[0] !== positions) {
    t0 = positions.reduce(_temp, 0);
    $[0] = positions;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const totalValueUsd = t0;
  let t1;
  if ($[2] !== positions) {
    t1 = positions.reduce(_temp2, 0);
    $[2] = positions;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const totalFeesUsd = t1;
  let t2;
  if ($[4] !== error || $[5] !== isLoading || $[6] !== positions || $[7] !== refetch || $[8] !== totalFeesUsd || $[9] !== totalValueUsd) {
    t2 = {
      positions,
      totalValueUsd,
      totalFeesUsd,
      isLoading,
      error,
      refetch
    };
    $[4] = error;
    $[5] = isLoading;
    $[6] = positions;
    $[7] = refetch;
    $[8] = totalFeesUsd;
    $[9] = totalValueUsd;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}

// Note: V2 and V3 position fetching is now handled by useFactoryPositions hook
// which directly queries factory contracts and uses multicall for efficiency

/**
 * Hook to get positions for a specific DEX
 */
function _temp2(sum_0, pos_0) {
  return sum_0 + pos_0.feesValueUsd;
}
function _temp(sum, pos) {
  return sum + pos.totalValueUsd;
}
export function useDexPositions(dexSlug, chainId) {
  const $ = _c(12);
  const {
    positions,
    isLoading,
    error,
    refetch
  } = useLiquidityPositions(chainId);
  let dexPositions;
  let t0;
  if ($[0] !== dexSlug || $[1] !== positions) {
    let t1;
    if ($[4] !== dexSlug) {
      t1 = pos => pos.dex === dexSlug;
      $[4] = dexSlug;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    dexPositions = positions.filter(t1);
    t0 = dexPositions.reduce(_temp3, 0);
    $[0] = dexSlug;
    $[1] = positions;
    $[2] = dexPositions;
    $[3] = t0;
  } else {
    dexPositions = $[2];
    t0 = $[3];
  }
  const totalValue = t0;
  let t1;
  if ($[6] !== dexPositions || $[7] !== error || $[8] !== isLoading || $[9] !== refetch || $[10] !== totalValue) {
    t1 = {
      positions: dexPositions,
      totalValueUsd: totalValue,
      isLoading,
      error,
      refetch
    };
    $[6] = dexPositions;
    $[7] = error;
    $[8] = isLoading;
    $[9] = refetch;
    $[10] = totalValue;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  return t1;
}

/**
 * Hook to get a specific position by ID
 */
function _temp3(sum, pos_0) {
  return sum + pos_0.totalValueUsd;
}
export function usePosition(positionId, chainId) {
  const $ = _c(9);
  const {
    positions,
    isLoading,
    error
  } = useLiquidityPositions(chainId);
  let t0;
  if ($[0] !== positionId || $[1] !== positions) {
    let t1;
    if ($[3] !== positionId) {
      t1 = pos => pos.id === positionId;
      $[3] = positionId;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = positions.find(t1);
    $[0] = positionId;
    $[1] = positions;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const position = t0;
  let t1;
  if ($[5] !== error || $[6] !== isLoading || $[7] !== position) {
    t1 = {
      position,
      isLoading,
      error
    };
    $[5] = error;
    $[6] = isLoading;
    $[7] = position;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}